import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { useAppStore } from '@/store/useAppStore'
import { useNow } from '@/hooks/useNow'
import { useParser } from './useParser'
import {
  DEFAULT_MIN_VOLUME_USD,
  DEFAULT_DISPLAY_LIMIT,
  DEFAULT_SORT,
  deriveRows,
  type ParserFilters,
  type SortState,
} from './model'
import { MetricsRow } from './MetricsRow'
import { FiltersBar } from './FiltersBar'
import { ParserTable } from './ParserTable'
import { Spinner } from '@/components/common/Spinner'
import { EmptyState } from '@/components/common/EmptyState'
import { AlertIcon, InfoIcon, LayersIcon, RadarIcon, SearchIcon } from '@/components/common/icons'

const INITIAL_FILTERS: ParserFilters = {
  search: '',
  minFundingPct: 0,
  minVolumeUsd: DEFAULT_MIN_VOLUME_USD,
  selectedExchanges: [],
}

export function ParserPage() {
  const { snapshot, loading, refreshing, error, refresh } = useParser()
  const exchanges = useAppStore((s) => s.exchanges)
  const idle = useAppStore((s) => s.engine?.idle ?? false)
  const idleReason = useAppStore((s) => s.engine?.idle_reason ?? null)
  const nowMs = useNow(1000)

  const [filters, setFilters] = useState<ParserFilters>(INITIAL_FILTERS)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
  const exInit = useRef(false)

  // Инициализируем выбор бирж всем списком из Registry, как только он известен.
  useEffect(() => {
    if (!exInit.current && exchanges.length > 0) {
      exInit.current = true
      setFilters((f) => ({ ...f, selectedExchanges: exchanges }))
    }
  }, [exchanges])

  const patchFilters = (patch: Partial<ParserFilters>) => setFilters((f) => ({ ...f, ...patch }))
  const onSort = (key: string, dir: 'asc' | 'desc') => setSort({ key, dir })

  const { rows, columns, total } = useMemo(
    () => deriveRows(snapshot, filters, sort),
    [snapshot, filters, sort],
  )

  // Клиентский лимит показа («Топ N»). Сервер уже отдаёт ≤ display_limit (100),
  // здесь — дополнительное сужение обзора без перезапроса.
  const [displayLimit, setDisplayLimit] = useState(DEFAULT_DISPLAY_LIMIT)
  const visibleRows = rows.slice(0, displayLimit)

  const filtersActive =
    filters.search.trim() !== '' ||
    filters.minFundingPct > 0 ||
    (exchanges.length > 0 && filters.selectedExchanges.length !== exchanges.length)

  const resetFilters = () =>
    setFilters({ ...INITIAL_FILTERS, selectedExchanges: exchanges })

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Парсинг</h1>
          <p className="page-head__sub">
            Обзор ставок фандинга по биржам · поиск общих инструментов для связки
          </p>
        </div>
      </div>

      <MetricsRow snapshot={snapshot} nowMs={nowMs} />

      <FiltersBar
        filters={filters}
        exchanges={exchanges}
        onChange={patchFilters}
        onRefresh={refresh}
        refreshing={refreshing}
      />

      <div className="panel">
        <div className="panel__head">
          <div>
            <div className="panel__title">Ставки фандинга</div>
            <div className="panel__subtitle">
              Сортировка по размаху фандинга («Фандинг итог»). Метрика информационная —
              не порог решения (решение — на этапе расчётов).
            </div>
          </div>
          <div className="panel__head-controls">
            <label className="topn">
              <span className="topn__label">Показывать</span>
              <select
                className="mini-select"
                value={displayLimit}
                onChange={(e) => setDisplayLimit(Number(e.target.value))}
                aria-label="Сколько строк показывать"
              >
                <option value={25}>Топ 25</option>
                <option value={50}>Топ 50</option>
                <option value={100}>Топ 100</option>
              </select>
            </label>
            <span className="badge badge--accent">{visibleRows.length}</span>
          </div>
        </div>

        <ParserBody
          loading={loading}
          error={error}
          idle={idle}
          idleReason={idleReason}
          hasSnapshot={snapshot !== null}
          rowCount={rows.length}
          commonPairs={snapshot?.common_pairs ?? 0}
          filtersActive={filtersActive}
          onResetFilters={resetFilters}
          onRetry={refresh}
        >
          <ParserTable rows={visibleRows} columns={columns} sort={sort} onSort={onSort} nowMs={nowMs} />
        </ParserBody>

        <div className="tbl-foot">
          <span>
            Показано <strong>{visibleRows.length}</strong> из {rows.length}
            {total >= DEFAULT_DISPLAY_LIMIT ? ` · сервер отдаёт топ-${DEFAULT_DISPLAY_LIMIT}` : ''}
          </span>
          <span className="note__icon" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', color: 'var(--text-dim)' }}>
            <InfoIcon size={13} /> деньги/ставки — как на бирже, без локального пересчёта
          </span>
        </div>
      </div>
    </>
  )
}

interface ParserBodyProps {
  loading: boolean
  error: string | null
  idle: boolean
  idleReason: string | null
  hasSnapshot: boolean
  rowCount: number
  commonPairs: number
  filtersActive: boolean
  onResetFilters: () => void
  onRetry: () => void
  children: ReactNode
}

function ParserBody({
  loading,
  error,
  idle,
  idleReason,
  hasSnapshot,
  rowCount,
  commonPairs,
  filtersActive,
  onResetFilters,
  onRetry,
  children,
}: ParserBodyProps) {
  if (loading && !hasSnapshot) {
    return (
      <div className="empty">
        <Spinner size={26} />
        <div className="empty__text">Загрузка данных парсера…</div>
      </div>
    )
  }

  if (error && !hasSnapshot) {
    return (
      <EmptyState
        icon={<AlertIcon size={22} />}
        title="Не удалось загрузить парсер"
        text={error}
        action={
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            Повторить
          </button>
        }
      />
    )
  }

  // Idle движка: <2 бирж с данными / нет кандидатов (§Этап 9).
  if (idle && commonPairs === 0) {
    return (
      <EmptyState
        icon={<LayersIcon size={22} />}
        title="Нет пересечений инструментов"
        text={idleReason ?? 'Для связки нужно минимум 2 биржи с общими инструментами.'}
      />
    )
  }

  if (commonPairs === 0) {
    return (
      <EmptyState
        icon={<RadarIcon size={22} />}
        title="Пока нет общих пар"
        text="Парсер ещё не нашёл инструментов, торгуемых на ≥2 биржах. Нажмите «Обновить»."
      />
    )
  }

  if (rowCount === 0) {
    return (
      <EmptyState
        icon={<SearchIcon size={22} />}
        title="Ничего не найдено"
        text="Под текущие фильтры не подходит ни одна монета."
        action={
          filtersActive ? (
            <button type="button" className="btn btn--sm" onClick={onResetFilters}>
              Сбросить фильтры
            </button>
          ) : undefined
        }
      />
    )
  }

  return <>{children}</>
}
